import type { ClipCandidate, TranscriptUnit } from "../ai/createClips";
import type { FindHighlightResult, TranscriptRangeHighlight } from "./types";

const PREVIEW_TEXT_MAX_LENGTH = 220;

const normalizeTimeMs = (value: unknown): number => {
    const ms = Math.round(Number(value ?? 0));
    return Number.isFinite(ms) ? Math.max(0, ms) : 0;
};

const normalizeDurationSec = (startMs: number, endMs: number): number => {
    const durationSec = Math.max(0, (endMs - startMs) / 1000);
    return Math.max(1, Math.round(durationSec));
};

const joinPreviewSegments = (segments: string[]): string =>
    segments
        .map((segment) => String(segment ?? "").trim())
        .filter(Boolean)
        .join(" ")
        .replace(/\s+([.,!?;:)\]])/g, "$1")
        .trim();

const truncatePreviewText = (text: string, maxLength: number = PREVIEW_TEXT_MAX_LENGTH): string => {
    const normalized = String(text ?? "").trim();
    if (normalized.length <= maxLength) return normalized;
    return `${normalized.slice(0, Math.max(0, maxLength - 1)).trimEnd()}...`;
};

const buildPreviewTextForCandidate = (candidate: ClipCandidate, transcriptUnits: TranscriptUnit[]): string => {
    const startMs = normalizeTimeMs(candidate?.startMs);
    const endMs = Math.max(startMs, normalizeTimeMs(candidate?.endMs));
    const overlappingUnits = transcriptUnits.filter((unit) => {
        const unitStartMs = normalizeTimeMs(unit?.startMs);
        const unitEndMs = Math.max(unitStartMs, normalizeTimeMs(unit?.endMs));
        return unitStartMs < endMs && unitEndMs > startMs;
    });

    const transcriptPreview = joinPreviewSegments(overlappingUnits.map((unit) => unit.text));
    if (transcriptPreview) return truncatePreviewText(transcriptPreview);

    const fallback = joinPreviewSegments([
        String(candidate?.previewText ?? ""),
        String(candidate?.description ?? ""),
        String(candidate?.title ?? ""),
    ]);
    return truncatePreviewText(fallback);
};

export const buildFindHighlightResultsFromCandidates = ({
    candidates,
    transcriptUnits,
}: {
    candidates: ClipCandidate[];
    transcriptUnits: TranscriptUnit[];
}): FindHighlightResult[] =>
    (Array.isArray(candidates) ? candidates : [])
        .map((candidate, index) => {
            const startMs = normalizeTimeMs(candidate?.startMs);
            const endMs = Math.max(startMs, normalizeTimeMs(candidate?.endMs));
            const previewText = buildPreviewTextForCandidate(candidate, transcriptUnits);
            if (!previewText || endMs <= startMs) return null;

            return {
                id: String(candidate?.id ?? `highlight_${index + 1}`),
                previewText,
                durationSec: normalizeDurationSec(startMs, endMs),
                startMs,
                endMs,
            } satisfies FindHighlightResult;
        })
        .filter(Boolean) as FindHighlightResult[];

export const buildTranscriptRangeHighlights = ({
    highlights,
    color,
}: {
    highlights: FindHighlightResult[];
    color: string;
}): TranscriptRangeHighlight[] =>
    (Array.isArray(highlights) ? highlights : [])
        .map((highlight) => {
            const startMs = normalizeTimeMs(highlight?.startMs);
            const endMs = Math.max(startMs, normalizeTimeMs(highlight?.endMs));
            if (endMs <= startMs) return null;

            return {
                id: String(highlight?.id ?? ""),
                color: String(color ?? "").trim() || "#FFF59D",
                startMs,
                endMs,
            } satisfies TranscriptRangeHighlight;
        })
        .filter(Boolean) as TranscriptRangeHighlight[];
